import * as client from '../../services/client';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../components/Loader';
import { updateBulkStatus } from 'src/store/actions/serviceRequests';
import { fetchAllTicketStatus } from 'src/store/actions/formOptions';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { fetchtAllowedTabList } from 'src/store/actions/tabSelections';
import download from 'downloadjs';
import { toast } from 'react-toastify';
import WithNavigation from 'src/layouts/WithNavigation';
import { ServiceRequestDetailHeader } from 'src/pages/ServiceRequest/components/ServiceRequestDetailHeader';
import ServiceRequestContent from 'src/pages/ServiceRequest/ServiceRequestContent';
import {
  ServiceRequestProvider,
  useServiceRequestActionsFromContext,
  useServiceRequestDataFromContext,
} from 'src/pages/ServiceRequest/Context/ServiceRequestContext';
import 'react-image-lightbox/style.css';
import './ServiceRequest.scss';
import './ServiceRequestDetails.scss';
import {
  ServiceRequestNotesProvider,
  useServiceRequestNotesActionsFromContext,
} from 'src/pages/ServiceRequest/Context/ServiceRequestNotesContext';

interface ServiceRequestProps {
  match: {
    params: {
      id: string;
    };
  };
  location: {
    state?: {
      activeTab?: number;
    };
  };
  history: any;
  navigateTo: any;
}

interface ServiceRequestState {
  showOverlay: boolean;
  isOpen: boolean;
  Open: boolean;
  showPopup: boolean;
  serviceRequestId: string;
  statusOverlay: boolean;
  statusScheduleOverlay: boolean;
  serviceRequestActualTime: string;
  serviceRequestEstimatedTime: string;
}

const ServiceRequestInner = ({
  match,
  location,
  history,
  navigateTo,
}: ServiceRequestProps) => {
  const [state, setState] = useState<ServiceRequestState>({
    showOverlay: false,
    isOpen: false,
    Open: false,
    showPopup: false,
    serviceRequestId: '',
    statusOverlay: false,
    statusScheduleOverlay: false,
    serviceRequestActualTime: '',
    serviceRequestEstimatedTime: '',
  });

  const serviceRequestId = match.params.id;

  const {
    refreshData: customRefreshData,
    updateStatus,
    saveScheduledAppointmentFromAPI,
    deleteServiceRequestFromAPI,
  } = useServiceRequestActionsFromContext();

  const { serviceRequest } = useServiceRequestDataFromContext();
  const { fetchNotes } = useServiceRequestNotesActionsFromContext();
  const { fetchDocuments } = useServiceRequestActionsFromContext();

  useEffect(() => {
    if (serviceRequest?.id) {
      fetchDocuments(serviceRequest.id);
    }
  }, [serviceRequest?.id]);

  const user: any = useSelector((state: any) => selectAuthenticatedUser(state));
  const ticketStatus = useSelector(
    (state: any) => state.formOptions.ticketStatus
  );
  const dispatch = useDispatch();

  const refreshData = async () => {
    if (user && serviceRequestId) {
      await customRefreshData(serviceRequestId);
    }

    dispatch(fetchAllTicketStatus());

    if (serviceRequest?.propertyId) {
      dispatch(fetchtAllowedTabList(serviceRequest.propertyId));
    }
  };

  useEffect(() => {
    refreshData();
  }, [match.params.id, user]);

  useEffect(() => {
    if (serviceRequest?.id) {
      fetchNotes(serviceRequest.id);
    }
  }, [serviceRequest?.id]);

  const downloadPdf = (id: string) => {
    client.getServiceRequestPdfById(id).then((response: any) => {
      if (response.ok || response.status === 200) {
        response.text().then((text: any) => {
          download(text.substring(1, text.length - 1));
        });
      }
    });
  };

  const handleDeleteServiceRequest = async (serviceRequestData: any) => {
    if (!serviceRequestData?.id) {
      toast.error('Invalid service request data');
      return;
    }

    const success: boolean = await deleteServiceRequestFromAPI(
      serviceRequestData.id
    );

    if (success) {
      history.push('/app/service-requests');
    }
  };

  const handleSaveScheduledAppointment = async (
    formData: any,
    serviceRequestId: string
  ) => {
    const status: any = ticketStatus.filter(
      (u: any) => u.name === formData.name
    );
    const appointmentData = {
      ...formData,
      LoginUserId: user.id,
      ticketId: serviceRequest.ticketId,
      userId: serviceRequest.serviceProvider.id,
      TicketStatusId: status[0].id,
    };

    await saveScheduledAppointmentFromAPI(appointmentData, serviceRequestId);
  };

  const handleStatusChange = async (
    statusFormData: any,
    serviceRequestId: string
  ) => {
    if (statusFormData.name === 'Assigned') {
      setState({ ...state, statusOverlay: true });
    } else if (statusFormData.name === 'Scheduled') {
      setState({ ...state, statusScheduleOverlay: true });
    } else if (statusFormData.name === 'Declined') {
      setState({
        ...state,
        showPopup: true,
        serviceRequestId: serviceRequestId,
      });
    } else if (statusFormData.name === 'Archive') {
      const role = user.roles[0].role.name;

      const archiveParams = {
        serviceRequest: [{ id: serviceRequestId }],
        name: statusFormData.name,
        userId: user.id,
      };

      const from = 'serviceRequest';
      dispatch(updateBulkStatus(archiveParams, role, 'params1', from));
    } else {
      const status: any = ticketStatus.filter(
        (u: any) => u.name === statusFormData.name
      );

      const statusData = {
        ...statusFormData,
        LoginUserId: user.id,
        TicketStatusId: status[0].id,
      };

      await updateStatus(statusData, serviceRequestId);
    }
  };

  const handleStatusAssign = async (
    statusFormData: any,
    serviceRequestId: string
  ) => {
    const status: any = ticketStatus.filter((u: any) => u.name === 'Assigned');

    const params = {
      LoginUserId: user.id,
      name: 'Assigned',
      ServiceProviderId: statusFormData.userId,
      TicketStatusId: status[0].id,
    };

    await updateStatus(params, serviceRequestId);
    setState({ ...state, statusOverlay: false });
  };

  const onSubmitPopup = async (type: string, data: any) => {
    if (type === 'cancel') {
      setState({ ...state, showPopup: false, serviceRequestId: '' });
    } else {
      const status = ticketStatus.filter((u: any) => u.name === 'Declined');
      const params = {
        name: 'Declined',
        Reason: data.description,
        ReasonType: data.reason,
        LoginUserId: user.id,
        TicketStatusId: status[0].id,
      };
      setState({ ...state, showPopup: false });

      await updateStatus(params, state.serviceRequestId);

      setState((prevState: ServiceRequestState) => ({
        ...prevState,
        serviceRequestId: '',
      }));
    }
  };

  const activeTab: number = useMemo(() => {
    if (location?.state?.activeTab !== undefined) {
      return location.state.activeTab;
    }

    const queryParams = new URLSearchParams(window.location.search);
    const tabParam: string = queryParams.get('activeTab');
    return tabParam ? Number(tabParam) : 0;
  }, [location]);

  const requestId: string = match.params.id;

  if (serviceRequest?.id) {
    return (
      <>
        {serviceRequest.loading ? <Loader center={true} /> : null}
        <WithNavigation
          title={
            serviceRequest.ticketNumber
              ? serviceRequest.ticketNumber
              : serviceRequest.ticketId
          }
          top={
            <ServiceRequestDetailHeader
              location={location}
              onEditServiceRequest={() =>
                setState({ ...state, showOverlay: true })
              }
              onDeleteServiceRequest={() =>
                handleDeleteServiceRequest(serviceRequest)
              }
              onDownloadPdf={() => downloadPdf(serviceRequest.id)}
              navigateTo={navigateTo}
              handleStatusChange={(
                statusFormData: any,
                serviceRequestId: any
              ) => handleStatusChange(statusFormData, serviceRequestId)}
            />
          }
          bottom={
            <ServiceRequestContent
              requestId={requestId}
              activeTab={activeTab}
              history={history}
              state={state}
              setState={setState}
              onSubmitPopup={onSubmitPopup}
              handleStatusAssign={handleStatusAssign}
              handleSaveScheduledAppointment={handleSaveScheduledAppointment}
              refreshData={refreshData}
              matchId={match.params.id}
            />
          }
        />
      </>
    );
  }

  return <Loader center={true} />;
};

const ServiceRequest = (props: ServiceRequestProps) => {
  return (
    <ServiceRequestProvider>
      <ServiceRequestNotesProvider>
        <ServiceRequestInner {...props} />
      </ServiceRequestNotesProvider>
    </ServiceRequestProvider>
  );
};

export default ServiceRequest;
